import * as Yup from 'yup';

// A customer's portal login is created from the customer: username = company name,
// password = matični broj. The API keeps both in step when the customer is edited;
// these helpers only describe that to the admin on the Edit Client screen.

const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_BYTES = 72; // bcrypt ignores everything past 72 bytes; the API counts UTF-8 bytes

const utf8Length = value => new TextEncoder().encode(value).length;

export const newLoginPasswordSchema = Yup.string()
  .test(
    'min-length-when-set',
    `Lozinka mora imati najmanje ${PASSWORD_MIN_LENGTH} karaktera.`,
    value => !value || (value.trim() !== '' && value.length >= PASSWORD_MIN_LENGTH),
  )
  .test(
    'max-bytes',
    `Lozinka je predugačka (najviše ${PASSWORD_MAX_BYTES} znaka; č, ć, š, ž, đ se računaju kao dva).`,
    value => !value || utf8Length(value) <= PASSWORD_MAX_BYTES,
  );

const trimmed = value => (value || '').trim();

// The name becomes the login username and the matični broj its password, so stray spaces must not reach the API.
export const trimLoginFields = values => ({
  ...values,
  nameOfTheLegalEntity: trimmed(values.nameOfTheLegalEntity),
  identificationNumber: trimmed(values.identificationNumber),
  email: trimmed(values.email),
});

export const identificationNumberChanged = (original, values) =>
  trimmed(values.identificationNumber) !== original.identificationNumber;

export const customerUpdateErrorMessage = (error) => {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message;
  if (status === 409 && serverMessage) {
    return serverMessage;
  }
  if (status === 403) {
    return 'Nemate dozvolu za izmenu klijenta.';
  }
  return 'Neuspešno ažuriranje!';
};

const usernameFollowsName = (access, original) =>
  access.username === original.nameOfTheLegalEntity;

// The username the login will have once the edited values are saved.
export const loginUsernameAfterSave = (access, original, values) => {
  const name = trimmed(values.nameOfTheLegalEntity);
  return usernameFollowsName(access, original) && name ? name : access.username;
};

export const loginChangeNotice = ({ access, original, values, passwordSet }) => {
  if (access?.customerLogin !== true) {
    return null;
  }
  const username = loginUsernameAfterSave(access, original, values);
  if (passwordSet) {
    return `Prijava: korisničko ime "${username}", nova lozinka je postavljena.`;
  }
  if (identificationNumberChanged(original, values)) {
    return `Prijava: korisničko ime "${username}", lozinka je novi matični broj ${trimmed(values.identificationNumber)}.`;
  }
  if (trimmed(values.nameOfTheLegalEntity) !== original.nameOfTheLegalEntity && usernameFollowsName(access, original)) {
    return `Novo korisničko ime za prijavu: "${username}".`;
  }
  return null;
};

// The customer was saved (and a changed matični broj already reset the password) but the new password was not set.
export const passwordFailureMessage = ({ error, access, original, values }) => {
  const reason = trimmed(error?.response?.data?.message).replace(/\.+$/, '');
  const failure = reason
    ? `Podaci su sačuvani, ali nova lozinka nije postavljena: ${reason}.`
    : 'Podaci su sačuvani, ali nova lozinka nije postavljena.';
  const loginState = loginChangeNotice({ access, original, values, passwordSet: false });
  const oldPasswordStillWorks = identificationNumberChanged(original, values) ? null : 'Stara lozinka i dalje važi.';
  return [failure, loginState, oldPasswordStillWorks].filter(Boolean).join(' ');
};
